// Cliente HTTP de la tienda. La API se llama por el mismo origen (/v1): en producción Caddy la pasa a la API y en
// desarrollo el proxy de Vite. Con sesión, ante un 401 se refresca una sola vez (aunque fallen varias peticiones a la
// vez, y también entre pestañas) y se reintenta.
import { leerSesionGuardada, rotarTokens, fijarSesion, useSesion } from './sesion';
import { SLUG } from './tienda-actual';
import type { Tokens } from './tipos';

export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    readonly codigo: string,
    mensaje: string,
    readonly detalles?: unknown,
  ) {
    super(mensaje);
  }
}

/** Sin conexión, o el servidor no respondió a tiempo. */
export class SinRed extends Error {
  constructor() {
    super('No hay señal. Revisá tu conexión y probá de nuevo.');
  }
}

export interface Opciones {
  metodo?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  cuerpo?: unknown;
  consulta?: Record<string, string | number | null | undefined>;
  /** Rutas públicas: no mandan el token ni refrescan. */
  sinSesion?: boolean;
  timeoutMs?: number;
  signal?: AbortSignal;
  /** La respuesta es texto (el XML de la factura electrónica), no JSON. */
  texto?: boolean;
}

/** Margen para refrescar antes de que venza el access (el reloj del celular puede estar un poco corrido). */
const MARGEN_VENCIMIENTO_MS = 30_000;

type OyenteAcceso = (codigo: string) => void;
const oyentesAcceso = new Set<OyenteAcceso>();
/** 403 con sesión (tienda cerrada, número sin acceso o negocio que ya no es suyo): la app vuelve a mirar el perfil. */
export const alNegarAcceso = (f: OyenteAcceso) => {
  oyentesAcceso.add(f);
  return () => {
    oyentesAcceso.delete(f);
  };
};

const MENSAJE_ESTADO: Record<number, string> = {
  429: 'Hiciste muchos intentos seguidos. Esperá un momento.',
  502: 'El servidor no responde. Probá de nuevo en un rato.',
  503: 'El servidor no responde. Probá de nuevo en un rato.',
  504: 'El servidor no responde. Probá de nuevo en un rato.',
};

export function construirUrl(ruta: string, consulta?: Opciones['consulta']): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(consulta ?? {}))
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const qs = p.toString();
  return `/v1${ruta}${qs ? `?${qs}` : ''}`;
}

async function pedir(url: string, o: Opciones, token: string | null): Promise<Response> {
  const ctrl = new AbortController();
  const corte = setTimeout(() => ctrl.abort(), o.timeoutMs ?? 20_000);
  const alAbortar = () => ctrl.abort();
  o.signal?.addEventListener('abort', alAbortar);
  try {
    return await fetch(url, {
      method: o.metodo ?? (o.cuerpo === undefined ? 'GET' : 'POST'),
      headers: {
        Accept: 'application/json',
        ...(o.cuerpo !== undefined && { 'Content-Type': 'application/json' }),
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: o.cuerpo === undefined ? undefined : JSON.stringify(o.cuerpo),
      signal: ctrl.signal,
    });
  } catch (e) {
    if (o.signal?.aborted) throw e;
    throw new SinRed();
  } finally {
    clearTimeout(corte);
    o.signal?.removeEventListener('abort', alAbortar);
  }
}

let refrescando: Promise<boolean> | null = null;

/**
 * Refresca la sesión una sola vez aunque la pidan varias peticiones a la vez. `usado` es el access que falló: si otra
 * pestaña ya rotó los tokens mientras tanto, se usan los nuevos sin volver a refrescar (reusar un refresh viejo cierra
 * la sesión del lado de la API).
 */
export function refrescarSesion(usado: string | null): Promise<boolean> {
  refrescando ??= conCandado(() => refrescarAhora(usado)).finally(() => {
    refrescando = null;
  });
  return refrescando;
}

function conCandado<T>(f: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  return locks?.request ? (locks.request(`tienda:${SLUG}:refresh`, f) as Promise<T>) : f();
}

async function refrescarAhora(usado: string | null): Promise<boolean> {
  const guardada = leerSesionGuardada();
  if (!guardada) {
    fijarSesion(null, 'vencida');
    return false;
  }
  if (usado && guardada.access_token !== usado && !vencida(guardada.expira_en)) {
    useSesion.setState({ sesion: guardada });
    return true;
  }
  let r: Response;
  try {
    // El preventista (decisión D60) entra con su usuario del personal: se refresca como en el panel.
    r = await fetch(guardada.vendedor ? '/v1/auth/refresh' : '/v1/tienda/publico/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refresh_token: guardada.refresh_token }),
    });
  } catch {
    throw new SinRed();
  }
  if (r.ok) {
    rotarTokens((await r.json()) as Tokens);
    return true;
  }
  const cuerpo = (await r.json().catch(() => null)) as { error?: { codigo?: string; mensaje?: string } } | null;
  if (r.status === 401 || r.status === 403 || r.status === 409) {
    fijarSesion(null, cuerpo?.error?.codigo === 'TOKEN_REVOCADO' ? 'revocada' : 'vencida');
    return false;
  }
  throw new ErrorApi(r.status, cuerpo?.error?.codigo ?? 'ERROR', cuerpo?.error?.mensaje ?? MENSAJE_ESTADO[503]!);
}

const vencida = (expiraEn: string | undefined, ahora = Date.now()) =>
  !!expiraEn && Date.parse(expiraEn) - MARGEN_VENCIMIENTO_MS <= ahora;

const sesionVencida = () => new ErrorApi(401, 'SESION_VENCIDA', 'Tu sesión venció. Entrá de nuevo.');

export async function api<T>(ruta: string, o: Opciones = {}): Promise<T> {
  const url = construirUrl(ruta, o.consulta);
  let r: Response;
  if (o.sinSesion) r = await pedir(url, o, null);
  else {
    let s = useSesion.getState().sesion;
    if (!s) throw sesionVencida();
    // Vencido de antemano (la app estuvo cerrada un rato): se refresca antes, sin gastar un 401.
    if (vencida(s.expira_en)) {
      if (!(await refrescarSesion(s.access_token))) throw sesionVencida();
      s = useSesion.getState().sesion!;
    }
    r = await pedir(url, o, s.access_token);
    if (r.status === 401) {
      if (!(await refrescarSesion(s.access_token))) throw sesionVencida();
      r = await pedir(url, o, useSesion.getState().sesion?.access_token ?? null);
    }
  }
  if (r.status === 204) return undefined as T;
  if (o.texto && r.ok) return (await r.text()) as T;
  const cuerpo = (await r.json().catch(() => null)) as {
    error?: { codigo: string; mensaje: string; detalles?: unknown };
  } | null;
  if (!r.ok) {
    const e = new ErrorApi(
      r.status,
      cuerpo?.error?.codigo ?? 'ERROR',
      cuerpo?.error?.mensaje ?? MENSAJE_ESTADO[r.status] ?? 'Algo salió mal. Probá de nuevo.',
      cuerpo?.error?.detalles,
    );
    if (r.status === 403 && !o.sinSesion) oyentesAcceso.forEach((f) => f(e.codigo));
    throw e;
  }
  return cuerpo as T;
}

/** Mensaje para mostrar de cualquier error de una petición. */
export function mensajeDeError(e: unknown): string {
  if (e instanceof SinRed || e instanceof ErrorApi) return e.message;
  return 'Algo salió mal. Probá de nuevo.';
}

/** Para pruebas: olvida un refresh en vuelo. */
export function _reiniciarRefresh(): void {
  refrescando = null;
}
